import {
  useListOwners,
  useListProperties,
  useListRecentDocuments,
  useListResidents,
  useListVendors,
  type EntityType,
  type RecentDocumentOut,
  type Permission,
} from '@myrentalmate/api-client';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { okData, okItems } from '../../lib/apiData';
import { fileSize, formatDateTime } from '../../lib/format';
import { useMe } from '../../lib/me';
import { useUnitLabels } from '../properties/useUnitLabels';
import { RecordExtras } from './RecordExtras';
import { useDownloadDocument } from './useDownloadDocument';

const RECORD_TYPES: {
  value: EntityType;
  label: string;
  plural: string;
  /** Where to add the first one. */
  to: string;
  read: Permission;
  manage: Permission;
}[] = [
  {
    value: 'property',
    label: 'Property',
    plural: 'properties',
    to: '/staff/properties',
    read: 'properties.read',
    manage: 'properties.manage',
  },
  {
    value: 'unit',
    label: 'Unit',
    plural: 'units',
    to: '/staff/properties',
    read: 'properties.read',
    manage: 'properties.manage',
  },
  {
    value: 'owner',
    label: 'Owner',
    plural: 'owners',
    to: '/staff/owners',
    read: 'owners.read',
    manage: 'owners.manage',
  },
  {
    value: 'resident',
    label: 'Resident',
    plural: 'residents',
    to: '/staff/residents',
    read: 'residents.read',
    manage: 'residents.manage',
  },
  {
    value: 'vendor',
    label: 'Vendor',
    plural: 'vendors',
    to: '/staff/vendors',
    read: 'vendors.read',
    manage: 'vendors.manage',
  },
];

/** The records of one type, as id and name. Only the chosen type is fetched. */
function useRecords(type: EntityType): { id: number; label: string }[] {
  const query = (enabled: boolean) => ({ query: { enabled } });
  const size = { page_size: 200 };
  const properties = useListProperties(size, query(type === 'property'));
  const owners = useListOwners(size, query(type === 'owner'));
  const residents = useListResidents(size, query(type === 'resident'));
  const vendors = useListVendors(size, query(type === 'vendor'));
  const unitLabels = useUnitLabels();
  return useMemo(() => {
    switch (type) {
      case 'property':
        return okItems(properties.data).map((p) => ({ id: p.id, label: p.name }));
      case 'unit':
        return [...unitLabels].map(([id, label]) => ({ id, label }));
      case 'owner':
        return okItems(owners.data).map((o) => ({ id: o.id, label: o.name }));
      case 'resident':
        return okItems(residents.data).map((r) => ({
          id: r.id,
          label: `${r.last_name}, ${r.first_name}`,
        }));
      case 'vendor':
        return okItems(vendors.data).map((v) => ({ id: v.id, label: v.name }));
      case 'lease':
      case 'work_order':
      case 'inspection':
      case 'violation':
      case 'legal_case':
      case 'staff_license':
        return []; // these keep their files on their own pages
    }
  }, [type, properties.data, owners.data, residents.data, vendors.data, unitLabels]);
}

function RecentFiles() {
  const recent = okData(useListRecentDocuments().data)?.items ?? [];
  const { download, downloadError } = useDownloadDocument();
  if (recent.length === 0) return null;
  const columns: DataColumn<RecentDocumentOut>[] = [
    { label: 'File', render: (d) => <Box sx={{ overflowWrap: 'anywhere' }}>{d.file_name}</Box> },
    { label: 'Attached to', render: (d) => d.record_label },
    { label: 'Size', render: (d) => fileSize(d.size_bytes) },
    { label: 'Added', render: (d) => formatDateTime(d.created_at) },
    {
      label: 'Actions',
      align: 'right',
      render: (d) => (
        <Button size="small" onClick={() => download(d.id)}>
          Download
        </Button>
      ),
    },
  ];
  return (
    <Stack spacing={1}>
      <Typography variant="h6">Recent Files</Typography>
      {downloadError && <Alert severity="error">{downloadError}</Alert>}
      <DataTable columns={columns} rows={recent} rowKey={(d) => d.id} />
    </Stack>
  );
}

/** One place to upload and find files: they always belong to a record, so choose the record. */
export function FilesPage() {
  const { data: me } = useMe();
  const types = RECORD_TYPES.filter((t) => me?.permissions.includes(t.read));
  const [typeValue, setTypeValue] = useState<EntityType>('property');
  const [chosenId, setChosenId] = useState<number | null>(null);
  const records = useRecords(typeValue);
  const type = types.find((t) => t.value === typeValue);
  const recordId = records.some((r) => r.id === chosenId) ? chosenId : (records[0]?.id ?? null);

  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">Files &amp; Notes</Typography>
        <Typography color="text.secondary">
          Files, notes and tags belong to a record. Choose one to see or add them.
        </Typography>
      </div>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label="Type of Record"
          value={typeValue}
          onChange={(e) => {
            setTypeValue(e.target.value as EntityType);
            setChosenId(null);
          }}
          sx={{ minWidth: 180 }}
        >
          {types.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        {records.length > 0 && (
          <TextField
            select
            size="small"
            label="Record"
            value={recordId ?? ''}
            onChange={(e) => setChosenId(Number(e.target.value))}
            sx={{ minWidth: 280 }}
          >
            {records.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {type && recordId === null && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" component={Link} to={type.to}>
              Go to {type.plural}
            </Button>
          }
        >
          There are no {type.plural} yet. Add one first, then come back to attach files.
        </Alert>
      )}
      {type && recordId !== null && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <RecordExtras
            key={`${type.value}-${recordId}`}
            entityType={type.value}
            entityId={recordId}
            canManage={me?.permissions.includes(type.manage) ?? false}
          />
        </Paper>
      )}
      <RecentFiles />
    </Stack>
  );
}
